import React, { useEffect, useState } from 'react'
import { Badge, Button, Row, Col, Card } from 'react-bootstrap'
import { Title } from '../../../../Templates/Title'
import { FiGift, FiInfo } from 'react-icons/fi'
import { deletePromotion, getPromotions, togglePromotion } from '../../../API/Promotions/promotions'
import { CreatePromotionModal } from './components/CreatePromotionModal'
import { Promotion } from '../../../API/Promotions/models'
import toast from 'react-hot-toast'
import { MdOutlineDelete, MdOutlineModeEdit, MdOutlineToggleOn, MdOutlineToggleOff } from 'react-icons/md'
import { openImage } from '../../../../Utils/images'

export const Promotions = () => {
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [showCreatePromotionModal, setShowCreatePromotionModal] = useState(false);
    const [promotionToEdit, setPromotionToEdit] = useState<Promotion | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchPromotions = async () => {
        setIsLoading(true);
        try {
            const result = await getPromotions();
            if (result?.promotions) {
                setPromotions(result.promotions);
            }
        } catch (error) {
            toast.error('Error al cargar las promociones');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleDeletePromotion = async (promotionId: string) => {
        try {
            const result = await deletePromotion(promotionId);
            if (result?.success) {
                toast.success('Promoción eliminada correctamente');
                fetchPromotions();
            }
        } catch (error) {
            toast.error('Error al eliminar la promoción');
            console.error(error);
        }
    }

    const handleTogglePromotion = async (promotion: Promotion) => {
        try {
            const result = await togglePromotion(promotion.id);
            if (result?.success) {
                toast.success(`Promoción ${promotion.isActive ? 'desactivada' : 'activada'} correctamente`);
                fetchPromotions();
            } else {
                toast.error(`Error al ${promotion.isActive ? 'desactivar' : 'activar'} la promoción`);
            }
        } catch (error) {
            console.error(error);
            toast.error(`Error inesperado al gestionar la promoción`);
        }
    };

    useEffect(() => {
        fetchPromotions();
    }, []);

    return (
        <div className="mt-4 pb-5">
            {showCreatePromotionModal && (
                <CreatePromotionModal 
                    show={showCreatePromotionModal} 
                    onHide={() => {
                        setShowCreatePromotionModal(false);
                        setPromotionToEdit(null);
                    }} 
                    fetchPromotions={fetchPromotions} 
                    promotion={promotionToEdit} 
                />
            )}
            
            <div className='d-flex justify-content-between align-items-center mb-4'>
                <Title 
                    title='Promociones' 
                    icon={<FiGift size={24} />} 
                    subtitle='Configura las promociones que se mostrarán en la aplicación' 
                />
                <Button 
                    variant="primary" 
                    onClick={() => setShowCreatePromotionModal(true)}
                    className="d-flex align-items-center gap-2"
                >
                    <FiGift size={16} />
                    Nueva promoción
                </Button>
            </div>

            {isLoading ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            ) : promotions.length === 0 ? (
                <Card className="bg-dark-50 border-secondary text-center p-5">
                    <FiGift size={60} className="text-secondary mb-3 mx-auto" />
                    <h4>No hay promociones configuradas</h4>
                    <p className="text-secondary mb-4">
                        Crea tu primera promoción para mostrarla en la aplicación
                    </p>
                    <Button 
                        variant="primary" 
                        className="mx-auto d-flex align-items-center gap-2 justify-content-center"
                        style={{ width: 'fit-content' }}
                        onClick={() => setShowCreatePromotionModal(true)}
                    >
                        <FiGift size={16} />
                        Crear promoción
                    </Button>
                </Card>
            ) : (
                <Row className="g-4">
                    {promotions.map((promotion) => (
                        <Col key={promotion.id} lg={6} className="mb-3">
                            <Card className="h-100 bg-dark-75 border-secondary shadow-sm hover-shadow">
                                <Card.Body className="p-0">
                                    <div className="d-flex flex-column h-100">
                                        {/* Encabezado con estado */}
                                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom border-secondary">
                                            <div className="d-flex align-items-center gap-2">
                                                <h5 className="mb-0 text-primary fw-bold">{promotion.name}</h5>
                                                {promotion.isActive ? (
                                                    <Badge bg="success" pill className="px-2 py-1">Activa</Badge>
                                                ) : (
                                                    <Badge bg="danger" pill className="px-2 py-1">Inactiva</Badge>
                                                )}
                                            </div>
                                            {promotion.code && (
                                                <div className="px-2 py-1 bg-darker rounded-2 border border-secondary">
                                                    <span className="text-secondary small">Código:</span>
                                                    <span className="ms-1 text-light small">{promotion.code}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Contenido principal */}
                                        <div className="d-flex p-3 flex-grow-1">
                                            {/* Imagen */}
                                            <div className="me-3">
                                                {promotion.image ? (
                                                    <div
                                                        onClick={() => openImage(promotion.image ?? '')}
                                                        role="button"
                                                        className="position-relative rounded-3 overflow-hidden"
                                                        style={{
                                                            width: '100px',
                                                            height: '100px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                        }}
                                                    >
                                                        <img
                                                            src={promotion.image}
                                                            alt={promotion.name}
                                                            className="img-fluid h-100 w-100 object-fit-cover"
                                                            style={{ transition: 'transform 0.3s' }}
                                                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center bg-darker rounded-3"
                                                        style={{
                                                            width: '100px',
                                                            height: '100px'
                                                        }}
                                                    >
                                                        <FiGift size={30} className="text-secondary" />
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Descripción */}
                                            <div className="flex-grow-1">
                                                <p className="text-light mb-2" style={{ 
                                                    whiteSpace: 'pre-line', 
                                                    lineHeight: '1.4', 
                                                    display: '-webkit-box', 
                                                    WebkitLineClamp: 3, 
                                                    WebkitBoxOrient: 'vertical', 
                                                    overflow: 'hidden' 
                                                }}>
                                                    {promotion.description}
                                                </p>
                                                
                                                {promotion.disclaimer && (
                                                    <div className="d-flex align-items-start gap-1 text-secondary small">
                                                        <FiInfo size={14} className="mt-1" />
                                                        <span>{promotion.disclaimer}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Acciones */}
                                        <div className="p-3 border-t border-secondary bg-darker">
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant={promotion.isActive ? "outline-danger" : "outline-success"}
                                                    size="sm"
                                                    className="flex-grow-1 d-flex align-items-center justify-content-center gap-1 text-white" 
                                                    onClick={() => handleTogglePromotion(promotion)}
                                                >
                                                    {promotion.isActive ? (
                                                        <>
                                                            <MdOutlineToggleOff size={18} />
                                                            <span>Desactivar</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MdOutlineToggleOn size={18} />
                                                            <span>Activar</span>
                                                        </>
                                                    )}
                                                </Button>
                                                
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                                                    onClick={() => {
                                                        setPromotionToEdit(promotion);
                                                        setShowCreatePromotionModal(true);
                                                    }}
                                                >
                                                    <MdOutlineModeEdit size={16} />
                                                    <span>Editar</span>
                                                </Button>
                                                
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                                                    onClick={() => handleDeletePromotion(promotion.id)}
                                                >
                                                    <MdOutlineDelete size={16} />
                                                    <span>Eliminar</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    )
}
